import React, { useCallback, useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, ClockIcon, ScaleIcon, PlusIcon, MinusIcon, RepeatIcon, ZapIcon, HelpCircleIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { shuffleOptions } from '../../../utils/deterministicShuffle';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { MULTIPLE_CHOICE_MODES, readMode } from '../../../content/questions/questionModes';
import { hapticManager } from '../../../utils/hapticManager';
// F-FE-4: Typed accessor for the loosely-typed `question.data` bag. Returns a
// readonly record (or empty fallback) so call sites never need `as any`. The
// Question discriminated union does not yet carry mode-specific data shapes
// (see RC audit), so this is the safest local mitigation: stay inside
// `unknown` and never widen to `any`.
function getQuestionData(question: unknown): Record<string, unknown> {
  const data = (question as {
    data?: unknown;
  } | null | undefined)?.data;
  return data && typeof data === 'object' ? data as Record<string, unknown> : {};
}
// Icon map for `icon_chips` mode. Keys are the lucide icon names that seeds may
// reference via data.optionIcons. Unknown names fall back to HelpCircleIcon.
const ICON_CHIP_MAP: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  ArrowRight: ArrowRightIcon,
  Clock: ClockIcon,
  Scale: ScaleIcon,
  Plus: PlusIcon,
  Minus: MinusIcon,
  Repeat: RepeatIcon,
  Zap: ZapIcon
};
function resolveChipIcon(name: string | undefined): ComponentType<SVGProps<SVGSVGElement>> {
  if (!name) return HelpCircleIcon;
  return ICON_CHIP_MAP[name] ?? HelpCircleIcon;
}
export function MultipleChoiceRenderer({
  question,
  onAnswer,
  onReadyChange,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  // Mode detection — read from question.data (seed-controlled). Default behaviour
  // is preserved for any seed that does NOT set data.mode. Unknown modes
  // intentionally fall through to default rendering (governance pipeline
  // catches them with TYPE_INVALID_MODE).
  const mode = readMode(question, MULTIPLE_CHOICE_MODES);
  const isIconChips = mode === 'icon_chips';
  const isTwoStepJudge = mode === 'two_step_judge';
  const optionIcons: string[] = useMemo(() => {
    const raw = getQuestionData(question).optionIcons;
    return Array.isArray(raw) ? raw as string[] : [];
  }, [question]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
    iconName?: string;
  }>>([]);
  useEffect(() => {
    const shuffled = shuffleOptions(question.options, question.id);
    // Attach optionIcons[originalIndex] so icons survive the shuffle.
    const withIcons = shuffled.map(opt => ({
      ...opt,
      iconName: optionIcons[opt.originalIndex]
    }));
    setShuffledOptions(withIcons);
    setSelectedOption(null);
  }, [question.id, question.options, optionIcons]);
  useEffect(() => {
    onReadyChange?.(selectedOption !== null);
  }, [selectedOption, onReadyChange]);
  const handleSelect = useCallback((index: number) => {
    hapticManager.hapticLight();
    setSelectedOption(index);
    if (shuffledOptions[index]) {
      onAnswer(shuffledOptions[index].originalIndex);
    }
  }, [shuffledOptions, onAnswer]);
  // Keyboard: 1-9 keys for quick selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // R-06: skip when typing in a field or when a modal is open above us
      const target = e.target as HTMLElement | null;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target && target.isContentEditable) {
        return;
      }
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
        return;
      }
      const key = parseInt(e.key);
      if (!isNaN(key) && key >= 1 && key <= 9) {
        const index = key - 1;
        if (index < shuffledOptions.length) {
          handleSelect(index);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shuffledOptions, handleSelect]);
  const isSelected = (idx: number) => selectedOption === idx;
  // ─── Mode: two_step_judge ────────────────────────────────────────────────
  // Two-stage MC (TAA Q4 idiom_literal_judge). Step 1 binary judge; if the
  // learner picks the trigger index, step 2 reveals. Final answer payload
  // is JSON: { step1, step2? }.
  if (isTwoStepJudge) {
    return <TwoStepJudgeView question={question} color={color} onAnswer={onAnswer} onReadyChange={onReadyChange} data-id="element-11765" data-mp="23sg9buw4jtm6" />;
  }
  // ─── Mode: icon_chips ────────────────────────────────────────────────────
  // Renders options as a responsive grid of icon-chip cards. Used by
  // signaalwoord-functie-style questions where each option is a discourse
  // function (oorzaak-gevolg, tijd, tegenstelling, toevoeging, …).
  if (isIconChips) {
    return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4" data-id="element-11766" data-mp="2cfsjqo6bxjme">
        {shuffledOptions.map((option, idx) => {
        const Icon = resolveChipIcon(option.iconName);
        const selected = isSelected(idx);
        return <motion.button key={option.originalIndex} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0,
          scale: selected ? [0.96, 1.02, 1] : 1
        }} transition={{
          delay: 0.2 + idx * 0.08,
          scale: {
            duration: 0.3
          }
        }} whileHover={{
          y: -2
        }} whileTap={{
          scale: 0.96
        }} onClick={() => handleSelect(idx)} className={`
                relative flex flex-col items-center justify-center gap-2 p-4 sm:p-5 rounded-2xl border-2 text-center font-medium transition-all duration-200 min-h-[120px] focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-400 focus-visible:ring-offset-2
                ${selected ? 'shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}
              `} style={selected ? {
          borderColor: color,
          backgroundColor: hexToRgba(color, 0.08),
          color: color,
          boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`
        } as TailwindCSSProperties : undefined} onMouseEnter={e => {
          if (!selected) {
            e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
          }
        }} onMouseLeave={e => {
          if (!selected) {
            e.currentTarget.style.borderColor = '';
          }
        }} aria-pressed={selected} data-id="element-11767" data-mp="2rdkwj8khrtww" data-mpi={idx}>
              <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition-colors ${selected ? '' : 'bg-slate-100 text-slate-500'}`} style={selected ? {
            backgroundColor: color,
            color: '#ffffff'
          } : undefined} data-id="element-11768" data-mp="1rcgipb02uta4">
                <Icon className="w-6 h-6" strokeWidth={2.25} data-id="element-11769" data-mp="1o2fabn248wsl" />
              </span>
              <span className="text-sm sm:text-base leading-tight" data-id="element-11770" data-mp="1o0b777c1k1td">
                <LatexRenderer content={option.text} data-id="element-11771" data-mp="1j43yx7w6ae89" />
              </span>
              {selected && <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white" style={{
            backgroundColor: color
          }} data-id="element-11772" data-mp="1f060z0il39gs">
                  <CheckIcon className="w-3 h-3" strokeWidth={3} data-id="element-11773" data-mp="10cunuu6r7hek" />
                </span>}
              {idx < 9 && <span className="absolute bottom-2 right-2 hidden sm:inline-flex bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold text-slate-400" data-id="element-11774" data-mp="vejiavnhrt25">
                  {idx + 1}
                </span>}
            </motion.button>;
      })}
      </div>;
  }
  // ─── Default mode: stacked option rows (unchanged behaviour) ─────────────
  return <div className="grid grid-cols-1 gap-4" data-id="element-11775" data-mp="2t3ol0xlzijcc">
      {shuffledOptions.map((option, idx) => <motion.button key={option.originalIndex} initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0,
      scale: isSelected(idx) ? [0.96, 1.02, 1] : 1
    }} transition={{
      delay: 0.3 + idx * 0.1,
      scale: {
        duration: 0.3
      }
    }} whileHover={{
      scale: 1.01,
      x: 4
    }} whileTap={{
      scale: 0.96
    }} onClick={() => handleSelect(idx)} className={`
            p-5 rounded-2xl text-left text-lg font-medium transition-all duration-200 border-2 min-h-[72px] flex items-center group relative focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-400 focus-visible:ring-offset-2
            ${isSelected(idx) ? 'shadow-md ring-2 ring-offset-2' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}
          `} style={isSelected(idx) ? {
      borderColor: color,
      backgroundColor: hexToRgba(color, 0.08),
      color: color,
      boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`,
      outlineColor: hexToRgba(color, 0.3),
      '--tw-ring-color': hexToRgba(color, 0.3)
    } as TailwindCSSProperties : {
      '--hover-border': hexToRgba(color, 0.4)
    } as React.CSSProperties} onMouseEnter={e => {
      if (!isSelected(idx)) {
        e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
      }
    }} onMouseLeave={e => {
      if (!isSelected(idx)) {
        e.currentTarget.style.borderColor = '';
      }
    }} data-id="element-11776" data-mp="2xyu6xwfdzy0f" data-mpi={idx}>
          <motion.span animate={{
        scale: isSelected(idx) ? [1, 1.3, 1] : 1
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className={`
              inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold mr-4 transition-colors
              ${!isSelected(idx) ? 'bg-slate-100 text-slate-500' : ''}
            `} style={isSelected(idx) ? {
        backgroundColor: color,
        color: '#ffffff'
      } : undefined} onMouseEnter={e => {
        if (!isSelected(idx)) {
          ;
          (e.currentTarget as HTMLElement).style.backgroundColor = hexToRgba(color, 0.15);
          (e.currentTarget as HTMLElement).style.color = color;
        }
      }} onMouseLeave={e => {
        if (!isSelected(idx)) {
          ;
          (e.currentTarget as HTMLElement).style.backgroundColor = '';
          (e.currentTarget as HTMLElement).style.color = '';
        }
      }} data-id="element-11777" data-mp="2ikjybti0hjsn">
            {isSelected(idx) ? <CheckIcon className="w-4 h-4" strokeWidth={3} data-id="element-11778" data-mp="x9xxqxipvp59" /> : String.fromCharCode(65 + idx)}
          </motion.span>
          <span className="flex-1" data-id="element-11779" data-mp="1l1v6gfqve125">
            <LatexRenderer content={option.text} data-id="element-11780" data-mp="1pyli8qvn6ahr" />
          </span>
          {idx < 9 && <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md text-xs font-bold text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-1 shadow-sm" data-id="element-11781" data-mp="1ba8l3qktillo">
              <kbd className="font-mono" data-id="element-11782" data-mp="23xbey5eju85l">{idx + 1}</kbd>
            </span>}
        </motion.button>)}
    </div>;
}
// ─── two_step_judge view ───────────────────────────────────────────────────
// Encapsulated as a sub-component so it has its own state machine without
// disturbing the default/icon_chips render path.
interface StepConfig {
  prompt?: string;
  options: string[];
  correctIndex: number;
}
function TwoStepJudgeView({
  question,
  color,
  onAnswer,
  onReadyChange
}: {
  question: QuestionRendererProps['question'];
  color: string;
  onAnswer: QuestionRendererProps['onAnswer'];
  onReadyChange?: QuestionRendererProps['onReadyChange'];
}) {
  const data = getQuestionData(question);
  const step1 = data.step1 as StepConfig | undefined;
  const step2 = data.step2 as StepConfig | undefined;
  const step2Trigger = typeof data.step2_ifIndex === 'number' ? data.step2_ifIndex : 1;
  const [step1Pick, setStep1Pick] = useState<number | null>(null);
  const [step2Pick, setStep2Pick] = useState<number | null>(null);
  // Reset when question changes
  useEffect(() => {
    setStep1Pick(null);
    setStep2Pick(null);
  }, [question.id]);
  const showStep2 = step1Pick === step2Trigger && !!step2;
  // Ready when:
  // - step1 chosen AND it does NOT trigger step2 (single-step path), OR
  // - step1 triggers step2 AND step2 chosen.
  const ready = step1Pick !== null && (showStep2 ? step2Pick !== null : true);
  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);
  // Keyboard: 1-9 keys for quick selection. Routes to step1 until it's
  // picked; once step2 is open, routes to step2 instead.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // R-06: skip when typing in a field or when a modal is open above us
      const target = e.target as HTMLElement | null;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target && target.isContentEditable) {
        return;
      }
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
        return;
      }
      const key = parseInt(e.key);
      if (isNaN(key) || key < 1 || key > 9) return;
      const idx = key - 1;
      if (showStep2 && step2) {
        if (idx < step2.options.length) setStep2Pick(idx);
      } else if (step1) {
        if (idx < step1.options.length) {
          setStep1Pick(idx);
          if (idx !== step2Trigger) setStep2Pick(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showStep2, step1, step2, step2Trigger]);
  // Emit a structured answer once ready. We encode as JSON so the answer
  // protocol can record both picks; correctness is judged by validator.
  // Guard against duplicate emits (re-renders with identical payload) so
  // usePracticeSession sees one canonical answer per definitive choice.
  const lastEmittedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || step1Pick === null) {
      lastEmittedRef.current = null;
      return;
    }
    const payload = JSON.stringify({
      step1: step1Pick,
      ...(showStep2 && step2Pick !== null ? {
        step2: step2Pick
      } : {})
    });
    if (lastEmittedRef.current === payload) return;
    lastEmittedRef.current = payload;
    onAnswer(payload);
  }, [ready, step1Pick, step2Pick, showStep2, onAnswer]);
  if (!step1) {
    return <div className="text-sm text-slate-500 italic" data-id="element-11783" data-mp="376b9xbhsh9mb">
        Vraag mist <code data-id="element-11784" data-mp="2sadjm9ujqzi0">data.step1</code> — kan niet renderen.
      </div>;
  }
  return <div className="flex flex-col gap-6" data-id="element-11785" data-mp="2xai0w23a3y9a">
      {/* Step 1 — binary judge */}
      <div data-id="element-11786" data-mp="2jaqilp5qbvph">
        {step1.prompt && <div id="two-step-judge-step1-label" className="mb-3 text-sm font-medium text-slate-600" data-id="element-11787" data-mp="2jbs2mj4q3sjc">
            Stap 1 — {step1.prompt}
          </div>}
        <div role="radiogroup" aria-labelledby={step1.prompt ? 'two-step-judge-step1-label' : undefined} aria-label={step1.prompt ? undefined : 'Stap 1 — kies een oordeel'} className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-11788" data-mp="hkf0g1u53yqz">
          {step1.options.map((label, idx) => {
          const selected = step1Pick === idx;
          return <motion.button key={idx} animate={selected ? {
            scale: [0.96, 1.02, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 0.3
          }} whileHover={{
            y: -2
          }} whileTap={{
            scale: 0.96
          }} onClick={() => {
            hapticManager.hapticLight();
            setStep1Pick(idx);
            if (idx !== step2Trigger) setStep2Pick(null);
          }} className={`relative p-5 sm:p-6 rounded-2xl border-2 text-base sm:text-lg font-semibold transition-all min-h-[88px] ${selected ? 'shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-sm'}`} style={selected ? {
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.08),
            color: color,
            boxShadow: `0 4px 14px ${hexToRgba(color, 0.15)}`
          } as TailwindCSSProperties : undefined} role="radio" aria-checked={selected} data-id="element-11789" data-mp="15g07ra0u31u1" data-mpi={idx}>
                <LatexRenderer content={label} data-id="element-11790" data-mp="9k1w6f3efu4c" />
                {selected && <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full text-white" style={{
              backgroundColor: color
            }} data-id="element-11791" data-mp="1fzm99za92wnw">
                    <CheckIcon className="w-3 h-3" strokeWidth={3} data-id="element-11792" data-mp="1v1ulbyveqm1p" />
                  </span>}
              </motion.button>;
        })}
        </div>
      </div>

      {/* Step 2 — meaning MC, only when triggered */}
      <AnimatePresence data-id="element-11793" data-mp="1hbr1fek034ff">
        {showStep2 && step2 && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25
      }} data-id="element-11794" data-mp="1h94thdt76mwo">
            <div id="two-step-judge-step2-label" className="mb-3 text-sm font-medium text-slate-600" data-id="element-11795" data-mp="ydes9st39uju">
              Stap 2 — {step2.prompt ?? 'Wat betekent het?'}
            </div>
            <div role="radiogroup" aria-labelledby="two-step-judge-step2-label" className="grid grid-cols-1 gap-3" data-id="element-11796" data-mp="2sqw4wkryh703">
              {step2.options.map((label, idx) => {
            const selected = step2Pick === idx;
            return <motion.button key={idx} animate={selected ? {
              scale: [0.96, 1.02, 1]
            } : {
              scale: 1
            }} transition={{
              duration: 0.3
            }} whileHover={{
              x: 2
            }} whileTap={{
              scale: 0.96
            }} onClick={() => {
              hapticManager.hapticLight();
              setStep2Pick(idx);
            }} className={`p-4 rounded-xl border-2 text-left text-base font-medium transition-all min-h-[60px] flex items-center ${selected ? 'shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`} style={selected ? {
              borderColor: color,
              backgroundColor: hexToRgba(color, 0.08),
              color: color
            } as TailwindCSSProperties : undefined} role="radio" aria-checked={selected} data-id="element-11797" data-mp="qiauc4tb61om" data-mpi={idx}>
                    <span className="flex-1" data-id="element-11798" data-mp="1zk43cm9hkxw3">
                      <LatexRenderer content={label} data-id="element-11799" data-mp="1zlxdibjb608u" />
                    </span>
                    {selected && <CheckIcon className="w-4 h-4 ml-3" strokeWidth={3} style={{
                color
              }} data-id="element-11800" data-mp="3700gkxlm1kpy" />}
                  </motion.button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}